import { useState, useEffect, useCallback, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import type { CalendarEvent } from "./calendar/types";
import { useCourseEvents } from "./calendar/use-course-events";
import { useSessionEvents } from "./calendar/use-session-events";
import { 
  addCalendarEvent as addEvent, 
  deleteCalendarEvent as deleteEvent,
  changeSessionStatus
} from "./calendar/event-operations";
import { supabase } from "@/lib/supabase";
import { useGlobalCache, createSupabaseSubscription } from "@/hooks/use-global-cache";

export type { CalendarEvent } from "./calendar/types";

export const useCalendarEvents = (
  currentDate: Date,
  isClientView: boolean,
  clientId: string | null
) => {
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  
  // Utilizziamo un ID statico per la cache di pendingRequests
  const cacheKey = isClientView 
    ? `pending_requests_client_${clientId}`
    : 'pending_requests_admin';
  
  // Utilizziamo useGlobalCache per i pending requests con cache più lunga
  const { 
    data: pendingRequestsCount = 0,
    refresh: refreshPendingRequests,
    isLoading: isLoadingPendingRequests
  } = useGlobalCache<number>(
    cacheKey,
    async () => {
      if (isClientView) return 0;
      
      try {
        const now = new Date();
        const { count, error } = await supabase
          .from('calendar_events')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')
          .gte('start_time', now.toISOString());
          
        if (error) throw error;
        
        return count || 0;
      } catch (error) {
        console.error('Errore nel conteggio delle richieste pendenti:', error);
        return 0;
      }
    },
    {
      validity: 24 * 60 * 60 * 1000,  // 24 ore
      staleTime: 24 * 60 * 60 * 1000, // 24 ore
      setupRealtimeSubscription: (notifyUpdate) => {
        return createSupabaseSubscription(
          supabase,
          'calendar_events',
          { filter: "status=eq.pending" },
          notifyUpdate
        );
      }
    }
  );

  // Recupera gli eventi dei corsi
  const { 
    courseEvents = [], 
    isLoadingCourses 
  } = useCourseEvents(currentDate, isClientView, clientId, setError, toast);

  // Recupera le sessioni personali
  const { 
    sessionEvents = [], 
    isLoadingSessions 
  } = useSessionEvents(currentDate, isClientView, clientId, setError, toast);

  // Combina tutti gli eventi, assicurandoci che gli array esistano
  const calendarEvents = useMemo(() => [
    ...(Array.isArray(courseEvents) ? courseEvents : []),
    ...(Array.isArray(sessionEvents) ? sessionEvents : [])
  ], [courseEvents, sessionEvents]);
  
  const isLoading = isLoadingCourses || isLoadingSessions || isLoadingPendingRequests;

  // Funzioni per aggiungere e eliminare eventi
  const addCalendarEvent = useCallback(
    async (eventData: Omit<CalendarEvent, "id">) => {
      return await addEvent(eventData, isClientView, (events) => {
        // Non facciamo nulla qui perché gli eventi verranno aggiornati
        // attraverso le sottoscrizioni realtime
      }, setError, toast);
    },
    [isClientView, toast]
  );

  const deleteCalendarEvent = useCallback(
    async (event: CalendarEvent) => {
      await deleteEvent(event, isClientView, (events) => {
        // Non facciamo nulla qui perché gli eventi verranno aggiornati
        // attraverso le sottoscrizioni realtime
      }, setError, toast);
    },
    [isClientView, toast]
  );

  const approveSessionRequest = useCallback(
    async (eventId: string) => {
      const success = await changeSessionStatus(eventId, 'approved', setError, toast);
      return success;
    },
    [toast]
  );

  const rejectSessionRequest = useCallback(
    async (eventId: string) => {
      const success = await changeSessionStatus(eventId, 'rejected', setError, toast);
      return success;
    },
    [toast]
  );

  return {
    calendarEvents,
    isLoading,
    error,
    pendingRequestsCount,
    addCalendarEvent,
    deleteCalendarEvent,
    approveSessionRequest,
    rejectSessionRequest,
    refreshPendingRequests
  };
};
